import {EventData} from "./dataLayer.js";
import {trackingService} from "./trackingService.js";
import {whenTimeout} from "../common/timeout.js";
import type {SlButton} from "@shoelace-style/shoelace/shoelace.js";

/**
 * Listen for these events on the document level.
 */
const documentEventTypes = ['click'];

export type EventType = string;

export type TrackingKey = string;

export type TrackingEventDefinition = {
    triggers: Array<{
        eventType: EventType,
        trackingKey: TrackingKey,
    }>,
    eventData: Array<EventData>,
}

export function init(): void {
    setUpDocumentListeners();
}

function setUpDocumentListeners() {
    for (const eventType of documentEventTypes) {
        document.addEventListener(eventType, onDocumentEvent);
    }
}

function onDocumentEvent(e: Event) {
    const trackable = findTrackableElement(e);
    if (! trackable) {
        return;
    }

    const {target, trackingKey} = trackable;

    const tracked = trackingService.trackEvent({
        eventType: e.type,
        trackingKey: trackingKey,
    });

    if (! tracked) {
        return;
    }

    giveTrackingChanceToComplete(target, e, tracked);
}

function findTrackableElement(event: Event): {target: HTMLElement, trackingKey: TrackingKey}|null {
    for (const item of event.composedPath()) {
        if (item instanceof HTMLElement && item.dataset.clxgtmTrackingKey) {
            return {
                target: item,
                trackingKey: item.dataset.clxgtmTrackingKey!,
            };
        }
    }

    return null;
}

function giveTrackingChanceToComplete(target: HTMLElement, event: Event, tracked: Promise<void>): void {
    // When a link is clicked, wait for the tracking event to be handled before
    // navigating to the new page. If the tracking event is not handled within
    // 1 second, navigate anyway.
    if (event.type === 'click' && (target.tagName === 'A' || target.tagName === 'SL-BUTTON')) {
        const link = target as HTMLAnchorElement|SlButton;
        if (! event.defaultPrevented && link.href && ! link.href.startsWith('#')) {
            event.preventDefault();
            Promise.any([tracked, whenTimeout(1000)]).then(() => {
                window.location.href = link.href;
            });
        }
    }
}
